import { EmptyState, ErrorState, FreshnessBar } from "../components/AdminPageStates";
import { useFounderProductQuality } from "../hooks/useFounderProductQuality";
import { FounderProductQualityMetric, FounderProductQualityStatus } from "../services/founderProductQualityService";

const LOADING_CARDS = ["Crash Free %", "Average Launch Time", "Average Feed Load", "Search Latency", "Payment Success Rate", "Message Delivery Success"];

function statusLabel(status: FounderProductQualityStatus) {
  switch (status) {
    case "healthy":
      return "Healthy";
    case "watch":
      return "Watch";
    case "action-needed":
      return "Action Needed";
    case "unavailable":
      return "Unavailable";
  }
}

function ProductQualityMetricCard({ metric }: { metric: FounderProductQualityMetric }) {
  return (
    <details className={`founder-product-quality-metric-card ${metric.status}`}>
      <summary>
        <span>
          <span className="founder-product-quality-metric-title">{metric.title}</span>
          <span className="founder-product-quality-metric-story">{metric.story}</span>
        </span>
        <strong>{metric.value}</strong>
      </summary>
      <div className="founder-product-quality-metric-detail">
        <p>{metric.detail}</p>
        <p>Source: {metric.source}</p>
        {metric.unavailableReason ? <p>{metric.unavailableReason}</p> : null}
      </div>
    </details>
  );
}

function LoadingCard({ title }: { title: string }) {
  return (
    <section className="founder-product-quality-metric-card loading-card" aria-busy="true">
      <p className="founder-product-quality-metric-title">{title}</p>
      <div className="skeleton skeleton-value" />
      <div className="skeleton skeleton-note" />
    </section>
  );
}

export default function FounderProductQuality() {
  const { data, error, isLoading, lastUpdated, refresh } = useFounderProductQuality();

  if (error) {
    return <ErrorState message="Unable to load Founder Product Quality. Check Firebase permissions and platform health telemetry." />;
  }

  if (!isLoading && !data) {
    return <EmptyState message="Product quality signals will appear after platform health telemetry is recorded." />;
  }

  return (
    <section className="founder-product-quality-page" aria-label="Founder Product Quality">
      <FreshnessBar
        dataSource="Read-only quality telemetry from adminAnalytics/platformHealthMetrics with bounded fallbacks from bookings, marketplace_transactions, notifications, and messages"
        isLive={false}
        lastUpdated={lastUpdated}
        onRefresh={() => {
          void refresh();
        }}
      />

      <header className="founder-product-quality-hero">
        <p className="eyebrow">Founder product quality</p>
        <h2>Does Chor feel reliable?</h2>
        <p>
          Product quality is summarized through crashes, launch speed, core load times, search, booking, payments,
          notifications, uploads, and message delivery.
        </p>
      </header>

      {isLoading || !data ? (
        <div className="founder-product-quality-metric-grid">
          {LOADING_CARDS.map((title) => (
            <LoadingCard key={title} title={title} />
          ))}
        </div>
      ) : (
        <>
          <section className={`founder-product-quality-health-card ${data.currentHealth.status}`} aria-label={`Current Product Health ${data.currentHealth.value}`}>
            <div>
              <p className="eyebrow">{data.currentHealth.title}</p>
              <h2>{data.currentHealth.value}</h2>
            </div>
            <span className={`status-badge ${data.currentHealth.status}`}>{statusLabel(data.currentHealth.status)}</span>
            <p>{data.currentHealth.story}</p>
          </section>

          <section className="founder-product-quality-section" aria-labelledby="quality-signals-heading">
            <div className="founder-product-quality-section-header">
              <p className="eyebrow">Quality signals</p>
              <h3 id="quality-signals-heading">What is the user feeling?</h3>
            </div>
            <div className="founder-product-quality-metric-grid">
              {data.metrics.map((metric) => (
                <ProductQualityMetricCard key={metric.id} metric={metric} />
              ))}
            </div>
          </section>
        </>
      )}
    </section>
  );
}
